import {AnalysisStatusEnum} from "./analysis-status";
import {GetAnalysisResponseDto, GetAnalysisTyreResponseDto} from "../dto/get-analysis-response-dto";
import {SpareTyreResultEnum} from "./spare-tyre-result-enum";
import {PostAnalysisResponseDto, PostAnalysisTyreAnalysisResponseDto} from "../dto/post-analysis-response-dto";
import {AnalysisStage} from "./analysis-stage";
import {Step} from "./step";

export class Analysis {
  id?: number;
  uuid?:string;
  status: AnalysisStatusEnum = AnalysisStatusEnum.STARTED;
  referenceTyres: TyreAnalysis[] = [];
  wearTyres: TyreAnalysis[] = [];
  postcode?: AnalysisPostcode;
  vehicleMileage?: number;
  spareTyreResult?: SpareTyreResult;
  vinResult?: VinResult;
  registrationCardResult?: RegistrationCardResult;

  constructor(dto?: GetAnalysisResponseDto) {
    if (dto) {
      this.id = dto.analysisId;
      this.spareTyreResult = dto.spareTyreResult;
      this.vinResult = dto.vinResult;
      this.registrationCardResult = dto.registrationCardResult;
      this.status = dto.status;
      this.referenceTyres = dto.referenceTyres.map(rt => new TyreAnalysis(rt));
      this.wearTyres = dto.wearTyres.map(wt => new TyreAnalysis(wt));
    }
  }

  /**
   * True when at least one tyre picture has been taken.
   *
   * Since POST /v1/web-app/analyses is idempotent, an analysis exists as soon as a
   * vehicle is known: its mere existence no longer means the diagnosis has started.
   * Takes a plain object because the analysis may come from the local storage.
   */
  public static isStarted(analysis?: Partial<Analysis>): boolean {
    return !!(analysis?.referenceTyres?.length || analysis?.wearTyres?.length);
  }

  public static isSpareTyreAnswered(analysis?: Partial<Analysis>): boolean {
    return analysis?.spareTyreResult?.stage === AnalysisStage.TERMINAL;
  }

  public static isSpareTyreReusable(analysis?: Partial<Analysis>): boolean {
    return !!analysis?.spareTyreResult?.vehicleAnalysisId
      && analysis.spareTyreResult.stage !== AnalysisStage.ABORTED;
  }

  public static create(analysisResponseDto: PostAnalysisResponseDto) {
    const analysis = new Analysis();
    if (analysisResponseDto) {
      analysis.id = analysisResponseDto.id;
      analysis.uuid = analysisResponseDto.analysisUuid;
      analysis.status = analysisResponseDto.status;
      analysis.referenceTyres = analysisResponseDto.referenceTyres.map(rt => TyreAnalysis.create(rt));
      analysis.wearTyres = analysisResponseDto.wearTyres.map(wt => TyreAnalysis.create(wt));
      analysis.spareTyreResult = analysisResponseDto.spareTyreResult ? {
        vehicleAnalysisId: analysisResponseDto.spareTyreResult.vehicleAnalysisId,
        stage: analysisResponseDto.spareTyreResult.stage
      } : undefined;
    }
    return analysis;
  }
}

export class TyreAnalysis {
  id?: number;
  status: AnalysisStatusEnum = AnalysisStatusEnum.STARTED;
  position?: number;
  outside: boolean = true;
  uploadUrl?: {
    url: string;
    fileName: string;
  }

  constructor(dto?: GetAnalysisTyreResponseDto) {
    if (dto) {
      this.id = dto.tyreAnalysisId;
      this.status = dto.status === AnalysisStatusEnum.AI_READY ? AnalysisStatusEnum.UPLOADED : dto.status;
      this.position = dto.position;
      this.outside = dto.outside;
      this.uploadUrl = dto.uploadUrl;
    }
  }

  public static create(tyreAnalysisResponseDto: PostAnalysisTyreAnalysisResponseDto) {
    const tyreAnalysis = new TyreAnalysis();
    tyreAnalysis.id = tyreAnalysisResponseDto.id;
    tyreAnalysis.status = tyreAnalysisResponseDto.status === AnalysisStatusEnum.AI_READY ? AnalysisStatusEnum.UPLOADED : tyreAnalysisResponseDto.status;
    tyreAnalysis.position = Step.getPositionFromTyrePositionDto(tyreAnalysisResponseDto.position);
    return tyreAnalysis;
  }
}

export interface SpareTyreResult {
    spareTyreResult?: SpareTyreResultEnum | null,
    vehicleAnalysisId: number,
    stage: AnalysisStage
}

export interface VinResult {
  vehicleAnalysisId: number,
  status: AnalysisStatusEnum,
  vin: string,
  uploadUrls: {
    url: string,
    fileName: string
  }[]
}

export interface RegistrationCardResult {
  vehicleAnalysisId: number,
  status: AnalysisStatusEnum,
  countryCode: string,
  fields: {key: string, label: string, value: string}[],
  uploadUrls: {
    url: string,
    fileName: string
  }[]
}

export class AnalysisPostcode {
  codePostal?: string;
  codeCommune?: string;
  nomCommune?: string;
  libelleAcheminement?: string;
}

export enum AnalysisType {
  VIN = 'VIN',
  REGISTRATION_CARD = 'REGISTRATION_CARD',
  SPARE_TYRE = 'SPARE_TYRE'
}
